import React, { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { programService } from '../services/api';
import {
  Program,
  ProgramPricingComponent,
  ProgramPricingPathway,
  ProgramPricingResponse,
  ProgramBreakEvenRow,
} from '../types';

const DEFAULT_COMPONENTS: ProgramPricingComponent[] = [
  { name: 'Airfare & Travel Insurance', cost_type: 'variable', amount: null },
  { name: 'Accommodation', cost_type: 'variable', amount: null },
  { name: 'Tuition', cost_type: 'fixed', amount: null },
  { name: 'Orientation', cost_type: 'group', amount: null },
  { name: 'Transportation', cost_type: 'variable', amount: null, parent_name: 'Orientation' },
  { name: 'Food & Beverages', cost_type: 'variable', amount: null, parent_name: 'Orientation' },
  { name: 'Class Materials', cost_type: 'variable', amount: null, parent_name: 'Orientation' },
  { name: 'Trips', cost_type: 'variable', amount: null, parent_name: 'Orientation' },
  { name: 'Administrative', cost_type: 'variable', amount: null, parent_name: 'Orientation' },
];

const formatCurrency = (value?: number | null) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const reorderPathways = <T extends { id?: number | null }>(next: T[], prev: T[]) => {
  if (!prev.length) return next;
  const nextById = new Map<number, T>();
  next.forEach((pkg) => {
    if (pkg.id != null) {
      nextById.set(pkg.id, pkg);
    }
  });
  const ordered: T[] = [];
  prev.forEach((pkg) => {
    if (pkg.id != null && nextById.has(pkg.id)) {
      ordered.push(nextById.get(pkg.id)!);
      nextById.delete(pkg.id);
    }
  });
  next.forEach((pkg) => {
    if (!ordered.includes(pkg) && (pkg.id == null || nextById.has(pkg.id))) {
      ordered.push(pkg);
      if (pkg.id != null) nextById.delete(pkg.id);
    }
  });
  return ordered;
};

type ProgramPricingManagementProps = {
  initialProgramId?: number;
  hideProgramSelect?: boolean;
  embedded?: boolean;
  showBundleDiscount?: boolean;
  bundleDiscountAmountOverride?: number | null;
  bundleDiscountDescriptionOverride?: string | null;
  cohortOverrides?: Array<{
    id: number;
    name: string;
    start_date?: string | null;
    end_date?: string | null;
    selling_price?: number | null;
    estimated_selling_price?: number | null;
  }>;
  onClose?: () => void;
  onPricingChange?: (payload: ProgramPricingResponse) => void;
};

export type ProgramPricingHandle = {
  savePricing: () => Promise<void>;
};

const ProgramPricingManagement = forwardRef<ProgramPricingHandle, ProgramPricingManagementProps>(({
  initialProgramId,
  hideProgramSelect = false,
  embedded = false,
  showBundleDiscount = true,
  bundleDiscountAmountOverride = null,
  bundleDiscountDescriptionOverride = null,
  cohortOverrides,
  onClose,
  onPricingChange,
}, ref) => {
  const { user } = useAuth();
  const location = useLocation();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [baseCurrency, setBaseCurrency] = useState('');
  const [components, setComponents] = useState<ProgramPricingComponent[]>([]);
  const [pathways, setPathways] = useState<Array<ProgramPricingPathway & { overridesById: Record<number, { actual: string; estimate: string }> }>>([]);
  const [breakEven, setBreakEven] = useState<ProgramBreakEvenRow[]>([]);
  const [bundleDiscountAmount, setBundleDiscountAmount] = useState('');
  const [bundleDiscountDescription, setBundleDiscountDescription] = useState('');

  useEffect(() => {
    if (!cohortOverrides || cohortOverrides.length === 0) return;
    setPathways((prev) => {
      const byId = new Map(cohortOverrides.map((cohort) => [cohort.id, cohort]));
      let changed = false;
      const next = prev.map((pathway) => {
        const type = pathway.pathway_type || 'track';
        if (type !== 'track' || pathway.id == null) return pathway;
        const source = byId.get(pathway.id);
        if (!source) return pathway;
        const nextName = source.name ?? pathway.name;
        const nextStart = source.start_date ?? pathway.start_date;
        const nextEnd = source.end_date ?? pathway.end_date;
        const nextSelling = source.selling_price ?? pathway.selling_price;
        const nextEstimated = source.estimated_selling_price ?? pathway.estimated_selling_price;
        if (
          nextName !== pathway.name
          || nextStart !== pathway.start_date
          || nextEnd !== pathway.end_date
          || nextSelling !== pathway.selling_price
          || nextEstimated !== pathway.estimated_selling_price
        ) {
          changed = true;
          return {
            ...pathway,
            name: nextName,
            start_date: nextStart,
            end_date: nextEnd,
            selling_price: nextSelling,
            estimated_selling_price: nextEstimated,
          };
        }
        return pathway;
      });
      return changed ? next : prev;
    });
  }, [cohortOverrides]);

  useEffect(() => {
    if (!user?.is_admin) return;
    const fetchPrograms = async () => {
      setLoadingPrograms(true);
      try {
        const list = await programService.getAllPrograms();
        setPrograms(list);
        if (list.length) {
          const params = new URLSearchParams(location.search);
          const preferredId = initialProgramId ?? Number(params.get('programId'));
          const match = list.find((program) => program.id === preferredId);
          setSelectedProgramId(match ? match.id : list[0].id);
        } else if (initialProgramId) {
          setSelectedProgramId(initialProgramId);
        }
      } catch (err) {
        setError('Failed to load programs.');
      } finally {
        setLoadingPrograms(false);
      }
    };
    fetchPrograms();
  }, [user, location.search, initialProgramId]);

  useEffect(() => {
    if (!selectedProgramId) return;
    const program = programs.find((item) => item.id === selectedProgramId) || null;
    setSelectedProgram(program);
    setBundleDiscountAmount(program?.bundle_discount_amount != null ? String(program.bundle_discount_amount) : '');
    setBundleDiscountDescription(program?.bundle_discount_description ?? '');
    const fetchPricing = async () => {
      setError(null);
      try {
        const data = await programService.getProgramPricing(selectedProgramId);
        const programCurrency = programs.find((program) => program.id === selectedProgramId)?.currency || '';
        setBaseCurrency(data.base_currency || programCurrency || '');
        setComponents(data.components.length ? data.components : DEFAULT_COMPONENTS);
        const cohortItems = data.cohorts ?? [];
        const extensionItems = data.extensions ?? [];
        const legacyPathways = (data as ProgramPricingResponse & { packages?: ProgramPricingPathway[] }).pathways
          ?? (data as ProgramPricingResponse & { packages?: ProgramPricingPathway[] }).packages
          ?? [];
        const combined = [
          ...cohortItems.map((cohort) => ({ ...cohort, pathway_type: 'track' as const })),
          ...extensionItems.map((extension) => ({ ...extension, pathway_type: 'addon' as const })),
          ...legacyPathways,
        ];
        const mappedPathways = combined.map((pathway) => {
          const computedDuration = computeDurationDays(pathway.start_date ?? null, pathway.end_date ?? null);
          return ({
            ...pathway,
            duration_days: computedDuration ?? pathway.duration_days ?? null,
            overridesById: pathway.overrides.reduce<Record<number, { actual: string; estimate: string }>>((acc, override) => {
              acc[override.component_id] = {
                actual: override.amount != null ? String(override.amount) : '',
                estimate: override.estimated_amount != null ? String(override.estimated_amount) : '',
              };
              return acc;
            }, {}),
          });
        });
        setPathways((prev) => reorderPathways(mappedPathways, prev));
        setBreakEven(data.break_even || []);
      } catch (err) {
        setError('Failed to load pricing details.');
      }
    };
    fetchPricing();
  }, [selectedProgramId, programs]);

  const groupedComponents = useMemo(() => components, [components]);
  const parentOptions = useMemo(() => (
    components.filter((component) => component.cost_type === 'group').map((component) => component.name)
  ), [components]);

  const handleComponentChange = (index: number, field: keyof ProgramPricingComponent, value: string) => {
    setComponents((prev) => prev.map((component, i) => {
      if (i !== index) return component;
      if (field === 'amount') {
        return { ...component, amount: value === '' ? null : Number(value) };
      }
      if (field === 'estimated_amount') {
        return { ...component, estimated_amount: value === '' ? null : Number(value) };
      }
      if (field === 'cost_type' && value === 'group') {
        return { ...component, cost_type: 'group', amount: null, estimated_amount: null };
      }
      return { ...component, [field]: value } as ProgramPricingComponent;
    }));
  };

  const addComponent = () => {
    setComponents((prev) => ([...prev, { name: '', cost_type: 'variable', amount: null }]));
  };

  const removeComponent = (index: number) => {
    setComponents((prev) => prev.filter((_, i) => i !== index));
  };

  const loadDefaults = () => {
    setComponents(DEFAULT_COMPONENTS);
  };

  const computeDurationDays = (startDate: string | null, endDate: string | null) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff + 1 : null;
  };

  const addPathway = (pathwayType: 'track' | 'addon') => {
    setPathways((prev) => ([
      ...prev,
      {
        id: undefined,
        name: '',
        pathway_type: pathwayType,
        description: '',
        start_date: null,
        end_date: null,
        duration_days: null,
        capacity: null,
        included_items: [],
        meeting_times: '',
        selling_price: null,
        estimated_selling_price: null,
        overrides: [],
        overridesById: {},
      },
    ]));
  };

  const removePathway = (index: number) => {
    setPathways((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePathwayChange = (
    index: number,
    field: 'name' | 'description' | 'start_date' | 'end_date' | 'duration_days' | 'capacity' | 'included_items' | 'meeting_times' | 'selling_price',
    value: string
  ) => {
    setPathways((prev) => prev.map((pathway, i) => {
      if (i !== index) return pathway;
      if (field === 'duration_days') {
        return { ...pathway, duration_days: value === '' ? null : Number(value) };
      }
      if (field === 'capacity') {
        return { ...pathway, capacity: value === '' ? null : Number(value) };
      }
      if (field === 'included_items') {
        const items = value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        return { ...pathway, included_items: items };
      }
      if (field === 'selling_price') {
        return { ...pathway, selling_price: value === '' ? null : Number(value) };
      }
      const nextPathway = { ...pathway, [field]: value } as ProgramPricingPathway & {
        overridesById: Record<number, { actual: string; estimate: string }>;
      };
      if (field === 'start_date' || field === 'end_date') {
        const nextStart = (field === 'start_date' ? value : pathway.start_date) ?? null;
        const nextEnd = (field === 'end_date' ? value : pathway.end_date) ?? null;
        nextPathway.duration_days = computeDurationDays(nextStart, nextEnd);
      }
      return nextPathway;
    }));
  };

  const promoteEstimates = () => {
    setComponents((prev) => prev.map((component) => ({
      ...component,
      amount: component.estimated_amount ?? component.amount ?? null,
    })));
    setPathways((prev) => prev.map((pathway) => ({
      ...pathway,
      overridesById: Object.fromEntries(
        Object.entries(pathway.overridesById).map(([componentId, values]) => ([
          Number(componentId),
          {
            actual: values.estimate !== '' ? values.estimate : values.actual,
            estimate: values.estimate,
          },
        ]))
      ) as Record<number, { actual: string; estimate: string }>,
    })));
  };

  const buildPayload = useCallback((): ProgramPricingResponse | null => {
    if (!selectedProgramId) return null;
    const normalizePathway = (pathway: ProgramPricingPathway & {
      overridesById: Record<number, { actual: string; estimate: string }>;
    }) => ({
      id: pathway.id ?? null,
      name: pathway.name,
      description: pathway.description ?? null,
      start_date: pathway.start_date ?? null,
      end_date: pathway.end_date ?? null,
      duration_days: pathway.duration_days ?? null,
      capacity: pathway.capacity ?? null,
      included_items: pathway.included_items ?? [],
      meeting_times: pathway.meeting_times ?? null,
      selling_price: pathway.selling_price ?? null,
      estimated_selling_price: pathway.estimated_selling_price ?? pathway.selling_price ?? null,
      overrides: Object.entries(pathway.overridesById)
        .filter(([, values]) => values.actual !== '' || values.estimate !== '')
        .map(([componentId, values]) => ({
          component_id: Number(componentId),
          amount: values.actual === '' ? null : Number(values.actual),
          estimated_amount: values.estimate === '' ? null : Number(values.estimate),
        })),
    });
    const cohortPayload = pathways
      .filter((pathway) => (pathway.pathway_type || 'track') === 'track')
      .map((pathway) => normalizePathway(pathway));
    const extensionPayload = pathways
      .filter((pathway) => (pathway.pathway_type || 'track') === 'addon')
      .map((pathway) => normalizePathway(pathway));
    return {
      program_id: selectedProgramId,
      base_currency: baseCurrency || null,
      components: components.map((component) => ({
        ...component,
        amount: component.amount ?? null,
        estimated_amount: component.estimated_amount ?? null,
      })),
      cohorts: cohortPayload,
      extensions: extensionPayload,
      break_even: breakEven,
    };
  }, [baseCurrency, breakEven, components, pathways, selectedProgramId]);

  useEffect(() => {
    if (!onPricingChange) return;
    const payload = buildPayload();
    if (payload) {
      onPricingChange(payload);
    }
  }, [onPricingChange, buildPayload]);

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    setError(null);

    try {
      const discountAmountValue = bundleDiscountAmount.trim() === '' ? null : Number(bundleDiscountAmount);
      await programService.updateProgram(payload.program_id, {
        bundle_discount_amount: Number.isNaN(discountAmountValue) ? null : discountAmountValue,
        bundle_discount_description: bundleDiscountDescription.trim() || null,
      });
      const response = await programService.updateProgramPricing(payload.program_id, payload);
      const programCurrency = programs.find((program) => program.id === selectedProgramId)?.currency || '';
      setBaseCurrency(response.base_currency || programCurrency || '');
      setComponents(response.components);
      const cohortItems = response.cohorts ?? [];
      const extensionItems = response.extensions ?? [];
      const legacyPathways = (response as ProgramPricingResponse & { packages?: ProgramPricingPathway[] }).pathways
        ?? (response as ProgramPricingResponse & { packages?: ProgramPricingPathway[] }).packages
        ?? [];
      const combined = [
        ...cohortItems.map((cohort) => ({ ...cohort, pathway_type: 'track' as const })),
        ...extensionItems.map((extension) => ({ ...extension, pathway_type: 'addon' as const })),
        ...legacyPathways,
      ];
      const mappedPathways = combined.map((pathway) => ({
        ...pathway,
        overridesById: pathway.overrides.reduce<Record<number, { actual: string; estimate: string }>>((acc, override) => {
          acc[override.component_id] = {
            actual: override.amount != null ? String(override.amount) : '',
            estimate: override.estimated_amount != null ? String(override.estimated_amount) : '',
          };
          return acc;
        }, {}),
      }));
      setPathways((prev) => reorderPathways(mappedPathways, prev));
      setBreakEven(response.break_even || []);
    } catch (err) {
      setError('Failed to save pricing updates.');
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    savePricing: async () => {
      await handleSave();
    },
  }));

  const trackPathways = useMemo(() => (
    pathways.filter((pathway) => (pathway.pathway_type || 'track') === 'track')
  ), [pathways]);

  const addonPathways = useMemo(() => (
    pathways.filter((pathway) => (pathway.pathway_type || 'track') === 'addon')
  ), [pathways]);

  const [selectedTrackIds, setSelectedTrackIds] = useState<number[]>([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState<number[]>([]);

  const estimateDurationDays = useCallback((pathway: ProgramPricingPathway & { overridesById?: Record<number, { actual: string; estimate: string }> }) => {
    if (pathway.duration_days != null) return pathway.duration_days;
    if (pathway.start_date && pathway.end_date) {
      const start = new Date(pathway.start_date);
      const end = new Date(pathway.end_date);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        return diff >= 0 ? diff + 1 : null;
      }
    }
    return null;
  }, []);

  const computeSelectionDurationDays = useCallback((items: ProgramPricingPathway[]) => {
    let earliest: number | null = null;
    let latest: number | null = null;
    for (const item of items) {
      if (item.start_date) {
        const start = new Date(item.start_date).getTime();
        if (!Number.isNaN(start)) {
          earliest = earliest == null ? start : Math.min(earliest, start);
        }
      }
      if (item.end_date) {
        const end = new Date(item.end_date).getTime();
        if (!Number.isNaN(end)) {
          latest = latest == null ? end : Math.max(latest, end);
        }
      }
    }
    if (earliest != null && latest != null && latest >= earliest) {
      return Math.floor((latest - earliest) / (1000 * 60 * 60 * 24)) + 1;
    }
    return items
      .map((pkg) => estimateDurationDays(pkg) ?? 0)
      .reduce((sum, days) => sum + days, 0);
  }, [estimateDurationDays]);

  const estimatePrice = (pathway: ProgramPricingPathway) => (
    pathway.selling_price ?? pathway.estimated_selling_price ?? 0
  );

  const selectedTracks = useMemo(() => (
    trackPathways.filter((pathway) => pathway.id && selectedTrackIds.includes(pathway.id))
  ), [trackPathways, selectedTrackIds]);

  const selectedAddons = useMemo(() => (
    addonPathways.filter((pathway) => pathway.id && selectedAddonIds.includes(pathway.id))
  ), [addonPathways, selectedAddonIds]);

  const totalDurationDays = useMemo(() => (
    computeSelectionDurationDays([...selectedTracks, ...selectedAddons])
  ), [selectedTracks, selectedAddons, computeSelectionDurationDays]);

  const totalPriceBeforeDiscount = useMemo(() => (
    [...selectedTracks, ...selectedAddons]
      .map((pkg) => estimatePrice(pkg))
      .reduce((sum, value) => sum + (value || 0), 0)
  ), [selectedTracks, selectedAddons]);

  const effectiveBundleDiscount = useMemo(() => {
    if (bundleDiscountAmountOverride != null) {
      return Math.max(0, bundleDiscountAmountOverride);
    }
    const raw = bundleDiscountAmount.trim();
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
  }, [bundleDiscountAmount, bundleDiscountAmountOverride]);

  const bundleDiscountDescriptionText = bundleDiscountDescriptionOverride ?? bundleDiscountDescription;

  const bundleEligible = useMemo(() => (
    selectedTracks.length >= 2
  ), [selectedTracks.length]);

  const bundleDiscountApplied = bundleEligible ? effectiveBundleDiscount : 0;

  const totalPrice = useMemo(() => (
    Math.max(0, totalPriceBeforeDiscount - bundleDiscountApplied)
  ), [totalPriceBeforeDiscount, bundleDiscountApplied]);

  if (!user?.is_admin) {
    return <div className="text-center py-20 text-red-400">Admin access required.</div>;
  }

  return (
    <div className={embedded ? 'w-full' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>


      <div className="mb-8 rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/85 dark:bg-slate-900/70 p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Optional Extensions</h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">Add-on trips students can choose.</p>
          </div>
          <button
            type="button"
            onClick={() => addPathway('addon')}
            className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50/60 dark:hover:bg-slate-800/60"
          >
            Add extension
          </button>
        </div>
        <div className="space-y-4">
          {addonPathways.map((pkg) => {
            const index = pathways.indexOf(pkg);
            return (
              <div key={`${pkg.id ?? 'new-addon'}-${index}`} className="rounded-xl border border-slate-200/70 dark:border-slate-700/70 p-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="text-xs uppercase text-slate-400 dark:text-slate-400">Extension name</label>
                    <input
                      value={pkg.name}
                      onChange={(event) => handlePathwayChange(index, 'name', event.target.value)}
                      className="mt-2 w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                      placeholder="Sacred Journeys Experience"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-400 dark:text-slate-400">Start date</label>
                    <input
                      type="date"
                      value={pkg.start_date ?? ''}
                      onChange={(event) => handlePathwayChange(index, 'start_date', event.target.value)}
                      className="mt-2 w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-400 dark:text-slate-400">End date</label>
                    <input
                      type="date"
                      value={pkg.end_date ?? ''}
                      onChange={(event) => handlePathwayChange(index, 'end_date', event.target.value)}
                      className="mt-2 w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-400 dark:text-slate-400">Duration (days)</label>
                    <input
                      type="number"
                      min="1"
                      value={pkg.duration_days ?? ''}
                      readOnly
                      className="mt-2 w-full rounded border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/60 dark:bg-slate-900/50 px-3 py-2 text-sm text-slate-700 dark:text-slate-200"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-400 dark:text-slate-400">Price ({baseCurrency || 'currency'})</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pkg.selling_price ?? ''}
                      onChange={(event) => handlePathwayChange(index, 'selling_price', event.target.value)}
                      className="mt-2 w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs uppercase text-slate-400 dark:text-slate-400">Description</label>
                    <input
                      type="text"
                      value={pkg.description ?? ''}
                      onChange={(event) => handlePathwayChange(index, 'description', event.target.value)}
                      className="mt-2 w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                      placeholder="Aceh & Doha experience"
                    />
                  </div>
                  <div className="flex items-end justify-end md:col-span-2">
                    <button
                      type="button"
                      onClick={() => removePathway(index)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Remove extension
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {addonPathways.length === 0 && (
            <div className="text-center text-slate-500 dark:text-slate-300">No extensions yet.</div>
          )}
        </div>
      </div>

      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-1">Program Pricing</h1>
          <p className="text-slate-600 dark:text-slate-300">Build components, study tracks, and optional extensions.</p>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50/60 dark:hover:bg-slate-800/60"
            >
              Close
            </button>
          )}
          <button
            type="button"
            onClick={promoteEstimates}
            className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50/60 dark:hover:bg-slate-800/60"
          >
            Promote estimates → actuals
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !selectedProgramId}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Pricing'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/85 dark:bg-slate-900/70 p-5 shadow-lg">
        <div className={`grid gap-4 ${hideProgramSelect ? 'md:grid-cols-4' : 'md:grid-cols-5'}`}>
          {!hideProgramSelect && (
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Program</label>
              <select
                value={selectedProgramId ?? ''}
                onChange={(event) => setSelectedProgramId(Number(event.target.value))}
                disabled={loadingPrograms}
                className="mt-2 w-full rounded-lg border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
              >
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Currency</label>
            <div className="mt-2 rounded-lg border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/60 dark:bg-slate-900/50 px-3 py-2 text-sm text-slate-700 dark:text-slate-200">
              {baseCurrency || '—'}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Estimated Participants</label>
            <div className="mt-2 rounded-lg border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/60 dark:bg-slate-900/50 px-3 py-2 text-sm text-slate-700 dark:text-slate-200">
              {selectedProgram?.seats_available ?? selectedProgram?.cohort_capacity ?? '—'}
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">Uses Seats Available from the program.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Actual Enrolled</label>
            <div className="mt-2 rounded-lg border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 bg-slate-50/60 dark:bg-slate-900/50">
              {selectedProgram?.enrolled_student_count ?? 0}
            </div>
          </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={loadDefaults}
              className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50/60 dark:hover:bg-slate-800/60"
            >
              Load default components
            </button>
          </div>
        </div>
      </div>

      {showBundleDiscount && (
        <div className="mb-8 rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/85 dark:bg-slate-900/70 p-5 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Bundle Discount</h2>
              <p className="text-sm text-slate-500 dark:text-slate-300">Optional discount when students combine tracks or extensions.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase text-slate-400 dark:text-slate-400">Discount Amount ({baseCurrency || 'currency'})</label>
              <input
                type="number"
                step="0.01"
                value={bundleDiscountAmount}
                onChange={(event) => setBundleDiscountAmount(event.target.value)}
                className="mt-2 w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-2 py-1 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                placeholder="0.00"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase text-slate-400 dark:text-slate-400">Discount Description</label>
              <input
                type="text"
                value={bundleDiscountDescription}
                onChange={(event) => setBundleDiscountDescription(event.target.value)}
                className="mt-2 w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-2 py-1 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                placeholder="Applies when students select multiple tracks"
              />
            </div>
          </div>
        </div>
      )}

      <div className="mb-8 rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/20 p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Selection Summary</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">Preview how student selections change total cost and duration.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-white/85 dark:bg-slate-900/70 p-3">
              <p className="text-xs uppercase text-slate-400 dark:text-slate-400">Total Duration</p>
              <p className="text-lg font-semibold text-slate-900 dark:text-white">
                {totalDurationDays ? `${totalDurationDays} days` : '—'}
              </p>
            </div>
            <div className="rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-white/85 dark:bg-slate-900/70 p-3">
              <p className="text-xs uppercase text-slate-400 dark:text-slate-400">Estimated Total</p>
              <p className="text-lg font-semibold text-slate-900 dark:text-white">
                {formatCurrency(totalPrice)}
              </p>
              {bundleDiscountApplied > 0 && (
                <p className="text-xs text-emerald-600 mt-1">
                  Bundle discount applied: -{formatCurrency(bundleDiscountApplied)}
                  {bundleDiscountDescriptionText ? ` · ${bundleDiscountDescriptionText}` : ''}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-white/85 dark:bg-slate-900/70 p-3">
            <p className="text-xs uppercase text-slate-400 dark:text-slate-400 mb-2">Core Study Tracks</p>
            {trackPathways.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-300">Add tracks below to preview selections.</p>
            ) : (
              <div className="space-y-2">
                {trackPathways.map((pkg) => (
                      <label key={`track-select-${pkg.id ?? pkg.name}`} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <input
                          type="checkbox"
                          checked={pkg.id != null ? selectedTrackIds.includes(pkg.id) : false}
                          onChange={(event) => {
                            if (pkg.id == null) return;
                            const pkgId = pkg.id;
                            setSelectedTrackIds((prev) => (
                              event.target.checked ? [...prev, pkgId] : prev.filter((id) => id !== pkgId)
                            ));
                          }}
                          className="rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/50"
                        />
                    <span className="font-medium">{pkg.name || 'Untitled track'}</span>
                    <span className="text-slate-500 dark:text-slate-300">
                      {estimateDurationDays(pkg) ? `${estimateDurationDays(pkg)} days` : '—'} · {formatCurrency(estimatePrice(pkg))}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-white/85 dark:bg-slate-900/70 p-3">
            <p className="text-xs uppercase text-slate-400 dark:text-slate-400 mb-2">Optional Extensions</p>
            {addonPathways.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-300">Add add-ons below to preview selections.</p>
            ) : (
              <div className="space-y-2">
                {addonPathways.map((pkg) => (
                  <label key={`addon-select-${pkg.id ?? pkg.name}`} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                    <input
                      type="checkbox"
                      checked={pkg.id != null ? selectedAddonIds.includes(pkg.id) : false}
                      onChange={(event) => {
                        if (pkg.id == null) return;
                        const pkgId = pkg.id;
                        setSelectedAddonIds((prev) => (
                          event.target.checked ? [...prev, pkgId] : prev.filter((id) => id !== pkgId)
                        ));
                      }}
                      className="rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/50"
                    />
                    <span className="font-medium">{pkg.name || 'Untitled extension'}</span>
                    <span className="text-slate-500 dark:text-slate-300">
                      {estimateDurationDays(pkg) ? `${estimateDurationDays(pkg)} days` : '—'} · {formatCurrency(estimatePrice(pkg))}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-8 rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/85 dark:bg-slate-900/70 p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Student Preview</h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">This mirrors the student selection experience.</p>
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-300">
            Total: {formatCurrency(totalPrice)} · {totalDurationDays ? `${totalDurationDays} days` : '—'}
            {bundleDiscountApplied > 0 && (
              <span className="text-emerald-600"> · Bundle discount applied</span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div>
              <p className="text-xs uppercase text-slate-400 dark:text-slate-400 mb-2">Choose your study track</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {trackPathways.length === 0 ? (
                  <div className="text-sm text-slate-500 dark:text-slate-300">No tracks yet.</div>
                ) : (
                  trackPathways.map((pkg) => (
                    <label key={`preview-track-${pkg.id ?? pkg.name}`} className="cursor-pointer rounded-xl border border-slate-200/70 dark:border-slate-700/70 bg-white/85 dark:bg-slate-900/70 p-4 shadow-sm hover:border-emerald-300 dark:hover:border-emerald-700">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-900 dark:text-white">{pkg.name || 'Untitled track'}</span>
                        <input
                          type="checkbox"
                          checked={pkg.id != null ? selectedTrackIds.includes(pkg.id) : false}
                          onChange={(event) => {
                            if (pkg.id == null) return;
                            const pkgId = pkg.id;
                            setSelectedTrackIds((prev) => (
                              event.target.checked ? [...prev, pkgId] : prev.filter((id) => id !== pkgId)
                            ));
                          }}
                          className="rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/50"
                        />
                      </div>
                      {pkg.description && (
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-300">{pkg.description}</p>
                      )}
                      <div className="mt-3 flex items-center justify-between text-xs text-slate-500 dark:text-slate-300">
                        <span>{estimateDurationDays(pkg) ? `${estimateDurationDays(pkg)} days` : '—'}</span>
                        <span className="font-semibold text-slate-900 dark:text-white">{formatCurrency(estimatePrice(pkg))}</span>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase text-slate-400 dark:text-slate-400 mb-2">Optional extensions</p>
              <div className="space-y-2">
                {addonPathways.length === 0 ? (
                  <div className="text-sm text-slate-500 dark:text-slate-300">No extensions yet.</div>
                ) : (
                  addonPathways.map((pkg) => (
                    <label key={`preview-addon-${pkg.id ?? pkg.name}`} className="flex items-center justify-between rounded-lg border border-slate-200/70 dark:border-slate-700/70 bg-white/85 dark:bg-slate-900/70 p-3">
                      <div>
                        <div className="text-sm font-medium text-slate-900 dark:text-white">{pkg.name || 'Untitled extension'}</div>
                        {pkg.description && <div className="text-xs text-slate-500 dark:text-slate-300 mt-1">{pkg.description}</div>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-300">
                        <span>{estimateDurationDays(pkg) ? `${estimateDurationDays(pkg)} days` : '—'}</span>
                        <span className="font-semibold text-slate-900 dark:text-white">{formatCurrency(estimatePrice(pkg))}</span>
                        <input
                          type="checkbox"
                          checked={pkg.id != null ? selectedAddonIds.includes(pkg.id) : false}
                          onChange={(event) => {
                            if (pkg.id == null) return;
                            const pkgId = pkg.id;
                            setSelectedAddonIds((prev) => (
                              event.target.checked ? [...prev, pkgId] : prev.filter((id) => id !== pkgId)
                            ));
                          }}
                          className="rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500/50"
                        />
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/60 dark:bg-slate-900/50 p-4 h-fit">
            <p className="text-xs uppercase text-slate-500 dark:text-slate-300 mb-2">Your Selection</p>
            <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
              {selectedTracks.map((pkg) => (
                <div key={`selection-track-${pkg.id ?? pkg.name}`} className="flex items-center justify-between">
                  <span>✔ {pkg.name || 'Untitled track'} ({estimateDurationDays(pkg) ? `${estimateDurationDays(pkg)} days` : '—'})</span>
                  <span className="font-semibold">{formatCurrency(estimatePrice(pkg))}</span>
                </div>
              ))}
              {selectedAddons.map((pkg) => (
                <div key={`selection-addon-${pkg.id ?? pkg.name}`} className="flex items-center justify-between">
                  <span>✔ {pkg.name || 'Untitled extension'} ({estimateDurationDays(pkg) ? `${estimateDurationDays(pkg)} days` : '—'})</span>
                  <span className="font-semibold">{formatCurrency(estimatePrice(pkg))}</span>
                </div>
              ))}
              {bundleDiscountApplied > 0 && (
                <div className="flex items-center justify-between text-emerald-700">
                  <span>
                    Bundle discount
                    {bundleDiscountDescriptionText ? ` · ${bundleDiscountDescriptionText}` : ''}
                  </span>
                  <span className="font-semibold">-{formatCurrency(bundleDiscountApplied)}</span>
                </div>
              )}
              {selectedTracks.length === 0 && selectedAddons.length === 0 && (
                <div className="text-sm text-slate-500 dark:text-slate-300">Select tracks and extensions to see totals.</div>
              )}
            </div>
            <div className="mt-3 border-t border-slate-200/70 dark:border-slate-700/70 pt-3 text-sm text-slate-600 dark:text-slate-300">
              <div className="flex items-center justify-between">
                <span>Total</span>
                <span className="font-semibold text-slate-900 dark:text-white">{formatCurrency(totalPrice)}</span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-300 mt-1">Duration: {totalDurationDays ? `${totalDurationDays} days` : '—'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8 rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/85 dark:bg-slate-900/70 p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Program Pricing</h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">Base cost components for the overall program.</p>
          </div>
          <button
            type="button"
            onClick={addComponent}
            className="rounded-lg border border-slate-200/70 dark:border-slate-700/70 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50/60 dark:hover:bg-slate-800/60"
          >
            Add component
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 dark:text-slate-300">
              <tr className="border-b border-slate-200/70 dark:border-slate-700/70">
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Estimated</th>
                <th className="px-3 py-2 text-left">Actual</th>
                <th className="px-3 py-2 text-left">Parent</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/70">
              {groupedComponents.map((component, index) => (
                <tr key={`${component.id ?? 'new'}-${index}`}>
                  <td className="px-3 py-2">
                    <input
                      value={component.name}
                      onChange={(event) => handleComponentChange(index, 'name', event.target.value)}
                      className="w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-2 py-1 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                      placeholder="Component name"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={component.cost_type}
                      onChange={(event) => handleComponentChange(index, 'cost_type', event.target.value)}
                      className="w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-2 py-1 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                    >
                      <option value="fixed">Fixed</option>
                      <option value="variable">Variable</option>
                      <option value="group">Group</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.01"
                      value={component.estimated_amount ?? ''}
                      onChange={(event) => handleComponentChange(index, 'estimated_amount', event.target.value)}
                      disabled={component.cost_type === 'group'}
                      className="w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-2 py-1 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 disabled:bg-slate-50/60 dark:disabled:bg-slate-900/50"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.01"
                      value={component.amount ?? ''}
                      onChange={(event) => handleComponentChange(index, 'amount', event.target.value)}
                      disabled={component.cost_type === 'group'}
                      className="w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-2 py-1 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100 disabled:bg-slate-50/60 dark:disabled:bg-slate-900/50"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={component.parent_name ?? ''}
                      onChange={(event) => handleComponentChange(index, 'parent_name', event.target.value)}
                      className="w-full rounded border border-slate-200/70 dark:border-slate-700/70 px-2 py-1 text-sm bg-white/90 dark:bg-slate-900/60 text-slate-900 dark:text-slate-100"
                    >
                      <option value="">None</option>
                      {parentOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeComponent(index)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              {groupedComponents.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-slate-500 dark:text-slate-300">
                    No components yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/85 dark:bg-slate-900/70 p-5 shadow-lg">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Break-even Summary</h2>
          <p className="text-sm text-slate-500 dark:text-slate-300">Exact and rounded student counts per track.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 dark:text-slate-300">
              <tr className="border-b border-slate-200/70 dark:border-slate-700/70">
                <th className="px-3 py-2 text-left">Pathway</th>
                <th className="px-3 py-2 text-left">Fixed (Est)</th>
                <th className="px-3 py-2 text-left">Fixed (Act)</th>
                <th className="px-3 py-2 text-left">Variable (Est)</th>
                <th className="px-3 py-2 text-left">Variable (Act)</th>
                <th className="px-3 py-2 text-left">Margin (Est)</th>
                <th className="px-3 py-2 text-left">Margin (Act)</th>
                <th className="px-3 py-2 text-left">BE Students (Est)</th>
                <th className="px-3 py-2 text-left">BE Students (Act)</th>
                <th className="px-3 py-2 text-left">BE Revenue (Est)</th>
                <th className="px-3 py-2 text-left">BE Revenue (Act)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/70">
              {breakEven.map((row) => (
                <tr key={row.pathway_id}>
                  <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{row.pathway_name}</td>
                  <td className="px-3 py-2">{formatCurrency(row.estimated_fixed_cost)}</td>
                  <td className="px-3 py-2">{formatCurrency(row.fixed_cost)}</td>
                  <td className="px-3 py-2">{formatCurrency(row.estimated_variable_cost)}</td>
                  <td className="px-3 py-2">{formatCurrency(row.variable_cost)}</td>
                  <td className="px-3 py-2">{formatCurrency(row.estimated_contribution_margin)}</td>
                  <td className="px-3 py-2">{formatCurrency(row.contribution_margin)}</td>
                  <td className="px-3 py-2">
                    {row.estimated_break_even_exact != null
                      ? `${row.estimated_break_even_exact.toFixed(2)} / ${row.estimated_break_even_rounded ?? '—'}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {row.break_even_exact != null
                      ? `${row.break_even_exact.toFixed(2)} / ${row.break_even_rounded ?? '—'}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {row.estimated_break_even_revenue_exact != null
                      ? `${formatCurrency(row.estimated_break_even_revenue_exact)} / ${formatCurrency(row.estimated_break_even_revenue_rounded)}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {row.break_even_revenue_exact != null
                      ? `${formatCurrency(row.break_even_revenue_exact)} / ${formatCurrency(row.break_even_revenue_rounded)}`
                      : '—'}
                  </td>
                </tr>
              ))}
              {breakEven.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-4 text-center text-slate-500 dark:text-slate-300">
                    No break-even data yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

export default ProgramPricingManagement;
